import { FC, useState } from 'react'

import { useQuery, useSuspenseQueries } from '@tanstack/react-query'

import { Cluster, Spinner, TextField, useToaster } from '@forto-dev/phoenix-ui-core'

import { RequiredActionV2 } from '@/features/workbench'
import { useLocalisation } from '@/utils/localisation'

import { houseShipmentByCodeQueryOptions } from '../../api/houseShipments.service'
import { shipmentCargoSummaryOptions, shipmentQueryOptions } from '../../api/useShipment'
import { useHouseShipmentSealNumberUpdate, useShipmentInformationUpdate } from '../../api/useShipmentInformationUpdate'

type ActionAddSealNumbersProps = {
  action: RequiredActionV2
  setSubmitCallback: (cb: (() => Promise<void>) | undefined) => void
}

export const ActionAddSealNumbers: FC<ActionAddSealNumbersProps> = ({ action, setSubmitCallback }) => {
  const toaster = useToaster()

  const [{ data: shipment }] = useSuspenseQueries({
    queries: [houseShipmentByCodeQueryOptions(action.entityId)],
  })

  const { data, isLoading } = useQuery(shipmentQueryOptions(action.entityId, !shipment.v2Flow))
  const { data: dataV2, isLoading: isLoadingV2 } = useQuery(
    shipmentCargoSummaryOptions(action.entityId, shipment.v2Flow)
  )
  const { translate } = useLocalisation()
  const updateMutation = useShipmentInformationUpdate()
  const updateMutationV2 = useHouseShipmentSealNumberUpdate()

  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const [sealNumberMap, setSealNumberMap] = useState<Record<string, string>>({})

  if (isLoading || isLoadingV2) {
    return (
      <Cluster x="center">
        <Spinner />
      </Cluster>
    )
  }

  if (!data && !dataV2) {
    return null
  }

  const updateSealNumbers = async (newSealMap: Record<string, string>) => {
    try {
      const containers = data?.containers.map((container) => ({
        ...container,
        sealNumber: newSealMap[container._id],
      }))
      await updateMutation.mutateAsync({
        shipmentId: action.entityId,
        data: {
          containers,
        },
      })
      toaster({
        title: translate('required-actions.dialog.sealNumberUpdateSuccess'),
        variant: 'positive',
      })
    } catch (error) {
      toaster({
        title: translate('required-actions.dialog.sealNumberUpdateError'),
        description: error instanceof Error ? error.message : translate('errorDescription'),
        variant: 'negative',
      })
    }
  }

  const updateV2SealNumbers = async (newSealMap: Record<string, string>) => {
    try {
      const updatePromises = Object.entries(newSealMap).map(async ([containerId, newSealNumber]) => {
        if (newSealNumber) {
          await updateMutationV2.mutateAsync({
            houseShipmentCode: action.entityId,
            houseCargoID: containerId,
            data: {
              sealNumber: newSealNumber,
            },
          })
        }
      })

      await Promise.all(updatePromises)

      toaster({
        title: translate('required-actions.dialog.sealNumberUpdateSuccess'),
        variant: 'positive',
      })

      setSealNumberMap({})
    } catch (error) {
      toaster({
        title: translate('required-actions.dialog.sealNumberUpdateError'),
        description: error instanceof Error ? error.message : translate('errorDescription'),
        variant: 'negative',
      })
    }
  }

  const onChange = (containerId: string, newSealNumber: string) => {
    const updatedErrors = { ...errors }
    const isValid = newSealNumber !== ''
    updatedErrors[containerId] = !isValid
    setSubmitCallback(undefined)

    if (isValid) {
      const newSealMap = { ...sealNumberMap, [containerId]: newSealNumber }
      setSealNumberMap(newSealMap)
      updatedErrors[containerId] = false

      let allSealNumbersFilled = false
      const noErrors = Object.values(updatedErrors).every((error) => !error)

      if (shipment.v2Flow && dataV2) {
        allSealNumbersFilled = dataV2.every((container) => newSealMap[container.ID])
      } else {
        allSealNumbersFilled = data?.containers.every((container) => newSealMap[container._id]) || false
      }

      if (allSealNumbersFilled && noErrors) {
        if (shipment.v2Flow && dataV2) {
          setSubmitCallback(() => updateV2SealNumbers(newSealMap))
        } else {
          setSubmitCallback(() => updateSealNumbers(newSealMap))
        }
      }
    }
    setErrors(updatedErrors)
  }

  if (shipment.v2Flow && dataV2) {
    return dataV2.map((container, index) => {
      const containerId = container.ID

      return (
        <TextField
          key={containerId}
          label={`#${index + 1} ${translate('required-actions.dialog.sealNumber')}`}
          required
          defaultValue={sealNumberMap[containerId]}
          placeholder={translate('required-actions.dialog.sealNumberPlaceholder')}
          onChange={(e) => onChange(containerId, e.target.value)}
          state={errors?.[containerId] ? 'negative' : undefined}
          hint={errors?.[containerId] ? translate('required-actions.dialog.sealValidationError') : undefined}
        />
      )
    })
  }

  return data?.containers.map((container, index) => {
    return (
      <TextField
        key={container._id}
        label={`#${index + 1} ${translate('required-actions.dialog.sealNumber')}`}
        required
        defaultValue={sealNumberMap[container._id]}
        placeholder={translate('required-actions.dialog.sealNumberPlaceholder')}
        onChange={(e) => onChange(container._id, e.target.value)}
        state={errors?.[container._id] ? 'negative' : undefined}
        hint={errors?.[container._id] ? translate('required-actions.dialog.sealValidationError') : undefined}
      />
    )
  })
}
